import { createTheme } from "@mui/material/styles";
// WL-001: design tokens (single source of truth) — spacing/radius/shadow/motion/
// typography/breakpoints/z-index. One-way import (tokens.js does NOT import this
// file) so there is no circular dependency.
import { tokens } from "../theme/tokens";
// U0a: MUI breakpoint values, declared identical to MUI v5 defaults so the
// theme, `mq.*`, and `useBreakpoint()` share ONE number set (see breakpoints.js).
import { BP_VALUES } from "./breakpoints";

export const colors = {
  primary: "#123051",
  secondary: "#99439D",
  black: "#0F0F0F",
  white: "#ffffff",
  textGrey: "#6B6F80",
  textSecondary: "#00000099",
  background: "#F6F7F9",
  borderColor: "#3b3b3b",
  lightGrey: "#BBBBBB",
  mediumGrey: "#9A999A",
  darkGrey: "#565556",
  darkBlue: "#0F52BA",
  lightBlue: "#D4E2F2",
  lightBorder: "#e1e1e1",
  primaryLight: "#7b8c9e",
  disabled: "#F0F0F0",
  error: "#f32a00",
  facebook: "#1877f2",
  green: "#00ba01",
  orange: "#FCF1DC",
  purple: "#81459B",
  lightPurple: "#C283DA",
  transparentPurple: "#9C27B01A",
  PrimaryPurple: "#9B5AF8",
  // Palest brand tint — one step BELOW PrimaryPurple100 (#EFE9FE, the "selected"
  // fill). Exists so a hover state can echo the brand without being mistaken for
  // the selected state (see NewSidebar MenuItemHeader).
  PrimaryPurple50: "#F7F4FE",
  PrimaryPurple100: "#EFE9FE",
  PrimaryPurple500: "#9B5AF8",
  PrimaryPurple300: "#CBB4FE",
  PrimaryPurple700: "#8126DB",
  PrimaryPurple800: "#6C1FB8",
  PrimaryPurple400: "rgba(170, 124, 251, 1)",
  PrimaryPurple900: "rgba(90, 27, 151, 1)",
  PrimaryPurple600: "rgba(143, 56, 239, 1)",
  purpleGradient: "#9B5AF8B5",
  LightGreenBackground: "#E8F8EB",
  PrimaryGreen: "#26A840",
  // AA-accessible dark green for status TEXT on LightGreenBackground tints.
  // PrimaryGreen (#26A840) as text on #E8F8EB is only 2.82:1 (FAILS AA); this
  // darkened step clears 4.5:1 (5.29:1) while reading as the same "public" green.
  // PrimaryGreen stays for fills/icons; on-tint text != fill (mirrors on-fill purple).
  PrimaryGreenDark: "#12752A",
  TimedWheel: "#FFFBED",
  StrokePrimary: "#667085",
  divider: "rgba(187, 187, 187, 1)",
  NeutralBackground: "rgba(246, 247, 249, 1)",
  Education: "rgba(41, 193, 140, 1)",
  TimedWheelColor: "rgba(196, 221, 129, 1)",
  Highlight: "rgba(239, 233, 254, 1)",
  yellowDark:"#F5CC3B",
  yellowLight:"#FFFBED",
  textBlue: "#123051",
  NeutralBorder: "#BBBBBB",
  yellowText: "#F79A00",
  yellowLighter: "#FFF4E3",
  purpleLighter: "#F5DAF7",
  purpleText: "#99439D",
  greenLighter: "#D6FEF0",
  greenText: "#29C18C",
  // AA-accessible danger red for over-limit WARNING TEXT on light surfaces.
  // The prior #ED4337 as text is only 3.85:1 on #FFFFFF and 3.68:1 on the
  // #F9FAFB token-meter surface (FAILS WCAG-AA 4.5:1 for normal text); this
  // darkened step clears it (5.62:1 / 5.38:1) while still reading as "danger".
  // Mirrors the PrimaryGreenDark pattern above (on-tint status text != fill).
  errorTextAA: "#C62828"
};

export const Muitheme = createTheme({
  // ISP-4018 · Wave-6 U0a: declare MUI's breakpoint VALUES explicitly so
  // `theme.breakpoints.*`, styled-components `mq.*` (utils/breakpoints.js) and
  // JS `useBreakpoint()` all read ONE number set. These are IDENTICAL to MUI
  // v5's defaults (xs:0 · sm:600 · md:900 · lg:1200 · xl:1536) — declaring them
  // is a no-op behavior change that makes the agreement explicit + lint-able.
  breakpoints: {
    values: BP_VALUES,
  },
  palette: {
    primary: {
      main: colors.PrimaryPurple, // or any other color from your palette
      light: colors.PrimaryPurple300,
      dark: colors.PrimaryPurple700,
    },
    secondary: {
      main: colors.secondary,
    },
    background: {
      default: colors.background,
      paper: colors.white,
    },
    text: {
      primary: colors.black,
      secondary: colors.textSecondary,
    },
  },
  // WL-001: on-brand font for all MUI components (was defaulting to Roboto).
  typography: {
    fontFamily: tokens.fontFamily, // 'DM Sans', sans-serif
  },
  // DL-004 (ISP-4018): the filled PRIMARY button is the app's on-fill purple.
  // palette.primary.main stays #9B5AF8 for ACCENTS (outlined borders, icons,
  // switches, text buttons); ONLY the CONTAINED-primary FILL darkens to
  // brand.primaryActive #8F38EF so white 14px/500 label text clears WCAG-AA
  // (5.28:1, vs the failing 4.03:1 of #9B5AF8). Single app-wide source so no
  // screen re-hardcodes the darkened fill. (on-fill != accent.)
  components: {
    // FOCUS RING (measured 2026-08-01 on live staging bundle main.ab6c5b14.js):
    // the Paid-Access upsell CTA read `outline: none 0px` after a REAL Tab press
    // at 390/768/1440 — `matchesFV` was true, so `:focus-visible` was active and
    // the control simply painted no indicator. The only thing that changed on
    // focus was MUI's own diffuse 9%-black ELEVATION shadow, which is not a focus
    // indicator (no boundary, no contrast floor, offset downward only).
    //
    // MuiButtonBase is the shared primitive for Button, IconButton, Chip, Tab,
    // MenuItem, ListItemButton, ToggleButton and every other clickable MUI
    // surface, so one rule here covers all of them — EXCEPT any call site that
    // passes its own `"&:focus-visible"` via `sx`/styled, which wins on
    // specificity+order. Measured 2026-08-01: 114 such sx/makeStyles blocks in 56
    // files. This is NOT full coverage; see src/styles/focusRing.css for the
    // measured exceptions and decision D8 in C:\iYuga\NEEDS-GORDON.md.
    //
    // The values come from the SAME CSS custom properties that
    // `src/styles/focusRing.css` defines, so the theme and the global sheet
    // cannot drift; the literals are only the fallback for a context where the
    // stylesheet has not loaded (e.g. a unit test rendering without it).
    MuiButtonBase: {
      styleOverrides: {
        root: {
          // The DOUBLE ring — white inner band (box-shadow) + purple outer band
          // (outline). Same two-property split, and for the same reason, as
          // src/styles/focusRing.css; read the long note there before changing
          // either. `--iy-focus-ring-keep` lets an elevated surface (Fab, Paper)
          // keep its resting shadow while focused; it defaults to a transparent
          // no-op shadow, NOT `none`, which is invalid inside a shadow list.
          "&.Mui-focusVisible": {
            outline: "var(--iy-focus-ring-width, 2px) solid var(--iy-focus-ring-color, #9B5AF8)",
            outlineOffset: "var(--iy-focus-ring-offset, 2px)",
            boxShadow:
              "0 0 0 var(--iy-focus-ring-offset, 2px) var(--iy-focus-ring-inner, #FFFFFF), " +
              "var(--iy-focus-ring-keep, 0 0 #0000)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: tokens.brand.primaryActive, // #8F38EF — AA 5.28:1 white text
          "&:hover": { backgroundColor: tokens.brand.primaryActiveHover }, // #7E28E0
        },
        // MEASURED live web-dev.iyuga.net/gallery/<uid> (WebKit + axe-core,
        // a11y-drive-gallery-webkit): outlined+primary buttons ("Create Wheel"
        // in the gallery header and wheels-grid toolbar) render their label/
        // icon in theme.palette.primary.main (#9B5AF8), which is only
        // 4.03:1 on #FFFFFF / 3.76:1 on #F6F7F9 — both fail WCAG AA's 4.5:1
        // floor for normal-size text (see src/styles/focusRing.css's contrast
        // table; #9B5AF8 clears the non-text 3:1 floor, not the text one).
        // tokens.brand.primaryActive (#8F38EF) is the same AA-safe step
        // already used for containedPrimary's fill above.
        outlinedPrimary: {
          color: tokens.brand.primaryActive, // #8F38EF — AA 5.28:1 white / 4.92:1 on #F6F7F9
        },
      },
    },
    // MODAL-BACKDROP (measured on live staging, deployed bundle main.345736b5):
    // the anon "Enter Password" dialog on View Wheel was UNCLICKABLE with a real
    // mouse — `document.elementFromPoint` at the Confirm button's centre returned
    // `DIV.MuiBackdrop-root MuiModal-backdrop`, i.e. the modal's OWN backdrop
    // painted ABOVE its dialog content and swallowed the click (the request never
    // fired; the app then bounced the user to /login). A DOM `.click()` worked, so
    // every prior pass — and every screenshot — looked fine.
    //
    // Cause: MUI's default backdrop slot is `ModalBackdrop = styled(Backdrop)({
    // zIndex: -1 })` (@mui/material 5.18.0 Modal/Modal.js:56-63,86). Passing the
    // RAW `Backdrop` as `BackdropComponent` replaces that styled slot and silently
    // drops `z-index: -1`. The backdrop is `position: fixed` (paints in the
    // positioned layer) while a plain modal "paper" div is `position: static`
    // (paints in the in-flow layer), so the backdrop wins the paint order no
    // matter that it comes first in the DOM.
    //
    // The call sites are fixed too (the raw `BackdropComponent` prop is gone), but
    // this rule is the SHARED PRIMITIVE: it re-asserts MUI's own contract on the
    // MuiModal root for EVERY modal, including any future one that re-introduces a
    // custom backdrop component. Scoped to a direct child of the modal root, so
    // standalone `<Backdrop>` usages elsewhere (which need a real z-index) are
    // untouched.
    MuiModal: {
      styleOverrides: {
        root: {
          "& > .MuiModal-backdrop": { zIndex: -1 },
        },
      },
    },
  },
  // WL-001: expose the full token set on the theme so any MUI-styled surface can
  // read spacing/radius/shadow/motion/etc via `theme.tokens.*`. Additive custom
  // key — MUI preserves it and it does not affect MUI's own spacing/shape scales.
  tokens: { ...tokens, colors },
});
